/**
 * =============================================================================
 * CONTRACTOR WORKSPACE - Unified Contractor Management Console
 * =============================================================================
 * CONSOLIDATES: Contractor Management, Performance, Compliance
 * 
 * Single workspace replacing 3+ separate pages.
 * Tab-driven navigation, inline actions, performance metrics.
 * 
 * Route: /phs/contractors?tab=overview|jobs|quotes|performance|compliance|finance
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Briefcase, FileText, TrendingUp, Shield, DollarSign,
  Search, Plus, RefreshCw, Eye, Edit, Ban, Phone, Mail,
  Star, Clock, CheckCircle, AlertTriangle, MoreHorizontal,
  ChevronRight, X, Award, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { PHSCommandLayout, TOKENS } from '@/components/phs/enterprise';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import { useContractorUrlState } from '@/hooks/useUrlState';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// WORKSPACE TABS
// =============================================================================
const WORKSPACE_TABS = [
  { key: 'overview', label: 'All Contractors', icon: Users },
  { key: 'jobs', label: 'Live Jobs', icon: Briefcase },
  { key: 'performance', label: 'Performance', icon: TrendingUp },
  { key: 'compliance', label: 'Compliance', icon: Shield },
  { key: 'finance', label: 'Financials', icon: DollarSign },
];

// Health score colors
const getHealthColor = (score) => {
  if (score >= 90) return { bg: 'bg-green-100', text: 'text-green-700', label: 'Excellent' };
  if (score >= 75) return { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Good' };
  if (score >= 50) return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Fair' };
  return { bg: 'bg-red-100', text: 'text-red-700', label: 'Poor' };
};

// =============================================================================
// WORKSPACE KPI STRIP
// =============================================================================
function ContractorKPIStrip({ metrics }) {
  const kpis = [
    { label: 'Active Contractors', value: metrics.active || 0, color: '#0D9488' },
    { label: 'Avg Response', value: `${metrics.avg_response || 0}h`, color: '#6366F1' },
    { label: 'Avg SLA Score', value: `${metrics.avg_sla || 0}%`, color: '#22C55E' },
    { label: 'Compliance Issues', value: metrics.compliance_issues || 0, color: '#DC2626' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {kpis.map((kpi, idx) => (
        <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs text-slate-500">{kpi.label}</p>
          <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// CONTRACTOR DETAIL DRAWER
// =============================================================================
function ContractorDetailDrawer({ contractor, open, onClose, onAction }) {
  if (!contractor || !open) return null;
  
  const health = getHealthColor(contractor.health_score || 75);

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800">Contractor Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center">
              <span className="text-teal-700 font-bold text-xl">
                {contractor.name?.charAt(0) || contractor.company_name?.charAt(0) || 'C'}
              </span>
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{contractor.company_name || contractor.name}</h3>
              <p className="text-sm text-slate-500">{contractor.primary_trade || contractor.trade_type}</p>
              <Badge className={`${health.bg} ${health.text} mt-1`}>
                {contractor.health_score || 75}% - {health.label}
              </Badge>
            </div>
          </div>

          {/* Contact */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Contact</CardTitle>
            </CardHeader>
            <CardContent className="py-2 px-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-slate-400" />
                <span>{contractor.email || '-'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-slate-400" />
                <span>{contractor.phone || '-'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Job Stats - REAL DATA */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Job Statistics</CardTitle>
            </CardHeader>
            <CardContent className="py-2 px-4">
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div className="bg-teal-50 rounded p-2">
                  <p className="text-lg font-bold text-teal-700">{contractor.active_jobs || 0}</p>
                  <p className="text-xs text-teal-600">Active</p>
                </div>
                <div className="bg-amber-50 rounded p-2">
                  <p className="text-lg font-bold text-amber-700">{contractor.overdue_jobs || 0}</p>
                  <p className="text-xs text-amber-600">Overdue</p>
                </div>
                <div className="bg-green-50 rounded p-2">
                  <p className="text-lg font-bold text-green-700">{contractor.completed_30d || 0}</p>
                  <p className="text-xs text-green-600">Done (30d)</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Jobs</span>
                  <span className="font-medium">{contractor.total_jobs || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Completion Rate</span>
                  <span className="font-medium">{contractor.completion_rate || 0}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Metrics - REAL DATA */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent className="py-2 px-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-green-50 rounded p-2">
                  <p className="text-lg font-bold text-green-700">{contractor.speed_score || 90}%</p>
                  <p className="text-xs text-green-600">Speed</p>
                </div>
                <div className="bg-blue-50 rounded p-2">
                  <p className="text-lg font-bold text-blue-700">{contractor.quality_score || 85}%</p>
                  <p className="text-xs text-blue-600">Quality</p>
                </div>
                <div className="bg-purple-50 rounded p-2">
                  <p className="text-lg font-bold text-purple-700">{contractor.sla_compliance || contractor.compliance_score || 95}%</p>
                  <p className="text-xs text-purple-600">SLA</p>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Avg Response Time</span>
                  <span className="font-medium">{contractor.avg_response_hours || 0}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Quote Turnaround</span>
                  <span className="font-medium">{contractor.avg_quote_turnaround_hours || 0}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Invoice Rejection</span>
                  <span className={`font-medium ${(contractor.invoice_rejection_rate || 0) > 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {contractor.invoice_rejection_rate || 0}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Quality Rating</span>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="font-medium">{contractor.quality_rating || 4.5}/5</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => onAction('assign-job', contractor)} className="bg-teal-600 hover:bg-teal-700">
              <Briefcase className="w-4 h-4 mr-2" /> Assign Job
            </Button>
            <Button variant="outline" onClick={() => onAction('contact', contractor)}>
              <Phone className="w-4 h-4 mr-2" /> Contact
            </Button>
            <Button variant="outline" onClick={() => onAction('edit', contractor)}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </Button>
            <Button variant="outline" onClick={() => onAction('suspend', contractor)} className="text-red-600 hover:text-red-700">
              <Ban className="w-4 h-4 mr-2" /> Suspend
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// OVERVIEW TAB
// =============================================================================
function OverviewTab({ contractors, loading, onRefresh, onAction, filters, setFilters }) {
  const filteredContractors = useMemo(() => {
    let result = contractors;
    if (filters.trade && filters.trade !== 'all') {
      result = result.filter(c => c.primary_trade === filters.trade || c.trade_type === filters.trade);
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(c =>
        c.name?.toLowerCase().includes(search) ||
        c.company_name?.toLowerCase().includes(search)
      );
    }
    return result;
  }, [contractors, filters]);

  const trades = useMemo(() => {
    const tradeSet = new Set(contractors.map(c => c.primary_trade || c.trade_type).filter(Boolean));
    return Array.from(tradeSet);
  }, [contractors]);

  return (
    <div data-testid="overview-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search contractors..."
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9 h-9"
          />
        </div>
        <Select value={filters.trade || 'all'} onValueChange={(v) => setFilters({ ...filters, trade: v })}>
          <SelectTrigger className="w-40 h-9">
            <SelectValue placeholder="All Trades" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trades</SelectItem>
            {trades.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button size="sm" onClick={() => onAction('add-contractor')}>
          <Plus className="w-4 h-4 mr-1" /> Add Contractor
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Contractor</th>
              <th className="text-left p-3 font-medium text-slate-600">Trade</th>
              <th className="text-left p-3 font-medium text-slate-600">Active / Overdue</th>
              <th className="text-left p-3 font-medium text-slate-600">Response</th>
              <th className="text-left p-3 font-medium text-slate-600">SLA %</th>
              <th className="text-left p-3 font-medium text-slate-600">Health</th>
              <th className="text-left p-3 font-medium text-slate-600 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredContractors.map(contractor => {
              const health = getHealthColor(contractor.health_score || 75);
              return (
                <tr 
                  key={contractor.id} 
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => onAction('view', contractor)}
                  data-testid={`contractor-row-${contractor.id}`}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-teal-700 font-semibold text-xs">
                          {contractor.name?.charAt(0) || contractor.company_name?.charAt(0) || 'C'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{contractor.company_name || contractor.name}</p>
                        <p className="text-xs text-slate-500">{contractor.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3"><Badge variant="outline" className="text-xs">{contractor.primary_trade || contractor.trade_type || 'General'}</Badge></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{contractor.active_jobs || 0}</span>
                      {(contractor.overdue_jobs || 0) > 0 && (
                        <Badge className="bg-red-100 text-red-700 text-xs">{contractor.overdue_jobs} overdue</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-slate-600">{contractor.avg_response_hours ? `${contractor.avg_response_hours}h` : '-'}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${(contractor.sla_compliance || 0) >= 90 ? 'bg-green-500' : (contractor.sla_compliance || 0) >= 70 ? 'bg-amber-500' : 'bg-red-500'}`} 
                          style={{ width: `${contractor.sla_compliance || 0}%` }} 
                        />
                      </div>
                      <span className="text-xs">{contractor.sla_compliance || 0}%</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge className={`${health.bg} ${health.text} text-xs`}>{contractor.health_score || 75}%</Badge>
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onAction('assign-job', contractor)}>
                          <Briefcase className="w-4 h-4 mr-2" /> Assign Job
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onAction('edit', contractor)}>
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onAction('suspend', contractor)} className="text-red-600">
                          <Ban className="w-4 h-4 mr-2" /> Suspend
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
            {filteredContractors.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">
                {loading ? 'Loading...' : 'No contractors found'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// PERFORMANCE TAB
// =============================================================================
function PerformanceTab({ contractors, loading }) {
  const sortedByPerformance = useMemo(() => {
    return [...contractors].sort((a, b) => (b.health_score || 0) - (a.health_score || 0));
  }, [contractors]);

  return (
    <div data-testid="performance-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Performers */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" /> Top Performers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {sortedByPerformance.slice(0, 5).map((c, idx) => {
                const health = getHealthColor(c.health_score || 75);
                return (
                  <div key={c.id} className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{c.company_name || c.name}</p>
                        <p className="text-xs text-slate-500">{c.primary_trade || c.trade_type} • {c.completed_30d || 0} jobs (30d)</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={`${health.bg} ${health.text}`}>{c.health_score || 75}%</Badge>
                      <p className="text-xs text-slate-500 mt-1">{c.sla_compliance || 0}% SLA</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Needs Attention - Contractors with overdue jobs or low scores */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Needs Attention
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {sortedByPerformance
                .filter(c => (c.overdue_jobs || 0) > 0 || (c.health_score || 100) < 70)
                .slice(0, 5)
                .map(c => {
                  const health = getHealthColor(c.health_score || 50);
                  return (
                    <div key={c.id} className="flex items-center justify-between p-3">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{c.company_name || c.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {(c.overdue_jobs || 0) > 0 && (
                            <Badge className="bg-red-100 text-red-700 text-xs">{c.overdue_jobs} overdue</Badge>
                          )}
                          <span className="text-xs text-slate-500">{c.primary_trade || c.trade_type}</span>
                        </div>
                      </div>
                      <Badge className={`${health.bg} ${health.text}`}>{c.health_score || 50}%</Badge>
                    </div>
                  );
                })}
              {sortedByPerformance.filter(c => (c.overdue_jobs || 0) > 0 || (c.health_score || 100) < 70).length === 0 && (
                <div className="p-4 text-center text-slate-400 text-sm">
                  <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
                  All contractors performing well
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Performance Table */}
      <Card className="mt-4">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm">Full Performance Rankings</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Rank</th>
                <th className="text-left p-3 font-medium text-slate-600">Contractor</th>
                <th className="text-left p-3 font-medium text-slate-600">Response</th>
                <th className="text-left p-3 font-medium text-slate-600">SLA %</th>
                <th className="text-left p-3 font-medium text-slate-600">Completion</th>
                <th className="text-left p-3 font-medium text-slate-600">Active / Overdue</th>
                <th className="text-left p-3 font-medium text-slate-600">Health</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedByPerformance.map((c, idx) => {
                const health = getHealthColor(c.health_score || 75);
                return (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="p-3 font-medium">{idx + 1}</td>
                    <td className="p-3">{c.company_name || c.name}</td>
                    <td className="p-3 text-slate-600">{c.avg_response_hours || 0}h</td>
                    <td className="p-3">
                      <span className={`font-medium ${(c.sla_compliance || 0) >= 90 ? 'text-green-600' : (c.sla_compliance || 0) >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                        {c.sla_compliance || 0}%
                      </span>
                    </td>
                    <td className="p-3 text-blue-600">{c.completion_rate || 0}%</td>
                    <td className="p-3">
                      <span className="text-slate-800">{c.active_jobs || 0}</span>
                      {(c.overdue_jobs || 0) > 0 && (
                        <span className="text-red-600 ml-1">/ {c.overdue_jobs}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge className={`${health.bg} ${health.text}`}>{c.health_score || 75}%</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// MAIN CONTRACTOR WORKSPACE
// =============================================================================
function ContractorWorkspace({ wyldecrestUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contractorId } = useParams();
  
  // URL-driven state
  const { getParam, updateParams } = useContractorUrlState();
  const activeTab = getParam('tab', 'overview');
  const urlSearch = getParam('search', '');
  const urlTrade = getParam('trade', '');
  const urlScore = getParam('score', '');
  
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({});
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);

  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch contractors with REAL performance metrics from backend
      const res = await axios.get(`${API}/api/contractors/performance/all`, { headers });
      const data = res.data;
      
      // Use enriched contractors with real metrics
      const allContractors = data.contractors || [];
      setContractors(allContractors);
      
      // Use aggregated metrics from backend
      setMetrics({
        active: data.metrics?.active_contractors || allContractors.filter(c => c.status !== 'suspended').length,
        avg_response: data.metrics?.avg_response_hours || 0,
        avg_sla: data.metrics?.avg_sla_compliance || 0,
        compliance_issues: data.metrics?.contractors_with_overdue || 0,
      });
    } catch (err) {
      console.error('Failed to fetch contractors with performance:', err);
      // Fallback to basic contractors endpoint
      try {
        const fallbackRes = await axios.get(`${API}/api/contractors`, { headers });
        const fallbackContractors = fallbackRes.data?.contractors || fallbackRes.data || [];
        
        // Add placeholder metrics for fallback
        const enriched = fallbackContractors.map(c => ({
          ...c,
          health_score: 75,
          sla_compliance: 85,
          avg_response_hours: 4,
          completed_30d: 0,
          active_jobs: 0,
          overdue_jobs: 0,
          speed_score: 85,
          quality_score: 80,
          compliance_score: 90,
          quality_rating: 4.5,
        }));
        
        setContractors(enriched);
        setMetrics({
          active: enriched.filter(c => c.status !== 'suspended').length,
          avg_response: 4,
          avg_sla: 85,
          compliance_issues: 0,
        });
      } catch (fallbackErr) {
        console.error('Fallback fetch also failed:', fallbackErr);
        toast.error('Failed to load contractors');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // URL-driven filtered contractors
  const filteredContractors = useMemo(() => {
    let result = [...contractors];
    
    // Search filter
    if (urlSearch) {
      const q = urlSearch.toLowerCase();
      result = result.filter(c => 
        c.name?.toLowerCase().includes(q) ||
        c.company_name?.toLowerCase().includes(q) ||
        c.primary_trade?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      );
    }
    
    // Trade filter
    if (urlTrade) {
      result = result.filter(c => c.primary_trade?.toLowerCase() === urlTrade.toLowerCase());
    }
    
    // Performance score filter
    if (urlScore) {
      switch (urlScore) {
        case 'excellent':
          result = result.filter(c => (c.health_score || 0) >= 90);
          break;
        case 'good':
          result = result.filter(c => (c.health_score || 0) >= 70 && (c.health_score || 0) < 90);
          break;
        case 'poor':
          result = result.filter(c => (c.health_score || 0) < 70);
          break;
        default:
          break;
      }
    }
    
    return result;
  }, [contractors, urlSearch, urlTrade, urlScore]);

  // URL-driven handlers
  const setActiveTab = (tab) => updateParams({ tab, search: '', trade: '', score: '' });
  const handleSearchChange = (value) => updateParams({ search: value });
  const handleTradeChange = (value) => updateParams({ trade: value === 'all' ? '' : value });
  const handleScoreChange = (value) => updateParams({ score: value === 'all' ? '' : value });

  const handleAction = (action, contractor) => {
    switch (action) {
      case 'view':
        setSelectedContractor(contractor);
        setShowDrawer(true);
        break;
      case 'assign-job':
        navigate(`/phs/helpdesk?action=new-job&contractor_id=${contractor.id}`);
        setShowDrawer(false);
        break;
      case 'add-contractor':
        setShowAddModal(true);
        break;
      case 'edit':
        setEditingContractor(contractor);
        setShowEditModal(true);
        setShowDrawer(false);
        break;
      case 'contact':
        // Open email client
        if (contractor?.email) {
          window.open(`mailto:${contractor.email}`, '_blank');
        } else {
          toast.error('No email address available');
        }
        break;
      case 'suspend':
        handleSuspendContractor(contractor);
        break;
      default:
        break;
    }
  };

  const handleSuspendContractor = async (contractor) => {
    if (!contractor?.id) return;
    if (!window.confirm(`Are you sure you want to suspend ${contractor.company_name || contractor.name}?`)) return;
    
    try {
      await axios.patch(`${API}/api/contractors/${contractor.id}`, 
        { status: contractor.status === 'suspended' ? 'active' : 'suspended' },
        { headers }
      );
      toast.success(contractor.status === 'suspended' ? 'Contractor reactivated' : 'Contractor suspended');
      fetchData();
      setShowDrawer(false);
    } catch (err) {
      toast.error('Failed to update contractor status');
    }
  };

  const handleAddContractor = async (data) => {
    try {
      await axios.post(`${API}/api/contractors`, data, { headers });
      toast.success('Contractor added');
      setShowAddModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add contractor');
    }
  };

  const handleEditContractor = async (data) => {
    if (!editingContractor?.id) return;
    try {
      await axios.patch(`${API}/api/contractors/${editingContractor.id}`, data, { headers });
      toast.success('Contractor updated');
      setShowEditModal(false);
      setEditingContractor(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update contractor');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('wyldecrest_token');
    localStorage.removeItem('wyldecrest_user');
    navigate('/admin/login');
  };

  return (
    <PHSCommandLayout user={wyldecrestUser} onLogout={handleLogout}>
      <div className="p-4 lg:p-6" data-testid="contractor-workspace">
        {/* Workspace Header */}
        <div className="mb-4">
          <h1 className="text-xl font-bold text-slate-800">Contractor Workspace</h1>
          <p className="text-sm text-slate-500">Manage contractors, track performance, and monitor compliance</p>
        </div>

        {/* KPI Strip */}
        <ContractorKPIStrip metrics={metrics} />

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto pb-px">
          {WORKSPACE_TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                  isActive 
                    ? 'border-teal-600 text-teal-600' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab
            contractors={filteredContractors}
            loading={loading}
            onRefresh={fetchData}
            onAction={handleAction}
            filters={{ search: urlSearch, trade: urlTrade || 'all' }}
            setFilters={(f) => {
              if (f.search !== undefined) handleSearchChange(f.search);
              if (f.trade !== undefined) handleTradeChange(f.trade);
            }}
          />
        )}
        {activeTab === 'performance' && (
          <PerformanceTab contractors={filteredContractors} loading={loading} />
        )}
        {(activeTab === 'jobs' || activeTab === 'compliance' || activeTab === 'finance') && (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} view - Data aggregation in progress</p>
          </div>
        )}

        {/* Contractor Detail Drawer */}
        <ContractorDetailDrawer
          contractor={selectedContractor}
          open={showDrawer}
          onClose={() => setShowDrawer(false)}
          onAction={handleAction}
        />

        {/* Add Contractor Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Contractor</DialogTitle>
            </DialogHeader>
            <ContractorForm 
              onSubmit={handleAddContractor}
              onCancel={() => setShowAddModal(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Edit Contractor Modal */}
        <Dialog open={showEditModal} onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) setEditingContractor(null);
        }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Contractor</DialogTitle>
            </DialogHeader>
            <ContractorForm 
              contractor={editingContractor}
              onSubmit={handleEditContractor}
              onCancel={() => {
                setShowEditModal(false);
                setEditingContractor(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </PHSCommandLayout>
  );
}

// =============================================================================
// CONTRACTOR FORM COMPONENT
// =============================================================================
function ContractorForm({ contractor, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    company_name: contractor?.company_name || '',
    name: contractor?.name || '',
    email: contractor?.email || '',
    phone: contractor?.phone || '',
    primary_trade: contractor?.primary_trade || '',
    regions_covered: contractor?.regions_covered?.join(', ') || '',
    emergency_availability: contractor?.emergency_availability || false,
  });
  const [submitting, setSubmitting] = useState(false);

  const TRADES = [
    'Gas Engineer', 'Electrician', 'Plumber', 'General Maintenance',
    'Roofing', 'Drainage', 'Fire Safety', 'Structural', 'Carpentry'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.email) {
      toast.error('Company name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        regions_covered: formData.regions_covered.split(',').map(r => r.trim()).filter(r => r),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
          <Input
            value={formData.company_name}
            onChange={(e) => setFormData({...formData, company_name: e.target.value})}
            placeholder="ABC Services Ltd"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="John Smith"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="info@abcservices.co.uk"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            placeholder="07700 900000"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Primary Trade</label>
        <Select 
          value={formData.primary_trade} 
          onValueChange={(v) => setFormData({...formData, primary_trade: v})}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select trade" />
          </SelectTrigger>
          <SelectContent>
            {TRADES.map(trade => (
              <SelectItem key={trade} value={trade}>{trade}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Regions Covered</label>
        <Input
          value={formData.regions_covered}
          onChange={(e) => setFormData({...formData, regions_covered: e.target.value})}
          placeholder="East Anglia, South East"
        />
        <p className="text-xs text-slate-500 mt-1">Comma-separated list of regions</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="emergency"
          checked={formData.emergency_availability}
          onChange={(e) => setFormData({...formData, emergency_availability: e.target.checked})}
          className="rounded border-slate-300"
        />
        <label htmlFor="emergency" className="text-sm text-slate-700">24/7 Emergency Availability</label>
      </div>
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : (contractor ? 'Update Contractor' : 'Add Contractor')}
        </Button>
      </div>
    </form>
  );
}

export default withWyldecrestAuth(ContractorWorkspace, 'helpdesk');
